import { useEffect, useState } from 'react';
import { FlexboxGrid, Table, Button, Tooltip, Whisper } from 'rsuite';
import styles from './FlexboxGridTable.module.scss';
import iconArrowUp from '../../../../../../assets/icons/icon_arrow_up-gray.svg';
import iconArrowDown from '../../../../../../assets/icons/icon_arrow_down-gray.svg';
import { DetailsProdutoContent, Paginated } from '../../types';
import { formatPercent, formatPrice, formatNumberWithThousandsSeparator } from '../../utils';
import { LoadingSpinerLocal } from '../../../../../../components';

const { Pagination } = Table;

const getCompetitividadeBackgroundColor = (value: number): string => {
  if (value <= 85 || value > 115) return '#7DB6F5';
  if ((value > 85 && value < 95) || (value > 105 && value <= 115)) return '#A5CCF8';
  if (value >= 95 && value <= 105) return '#D7E8FC';
  return '#D7E8FC';
};

export type FlexboxGridTableColumn<T = Record<string, unknown>> = {
  key: string;
  label: string;
  sortable?: boolean;
  colspan?: number;
  render?: (row: T) => React.ReactNode;
};

type FlexboxGridTableProps<T = Record<string, unknown>> = {
  data: Paginated<DetailsProdutoContent>;
  columns: FlexboxGridTableColumn<T>[];
  loading?: boolean;
  firstColumnColspan?: number;
  otherColumnsColspan?: number;
  renderTotal?: (displayed: number, total: number) => string;
  onPageChange?: (page: number) => void;
  onSort?: (column: string, type: 'ASC' | 'DESC') => void;
  onClickSources?: (row: DetailsProdutoContent) => void;
};

const SortableHeader = ({
  label,
  sortKey,
  activeSortColumn,
  sortType,
  onSort,
}: {
  label: string;
  sortKey: string;
  activeSortColumn: string;
  sortType: 'ASC' | 'DESC';
  onSort: (column: string, type: 'ASC' | 'DESC') => void;
}) => {
  const isActive = activeSortColumn === sortKey;
  const nextType = isActive && sortType === 'ASC' ? 'DESC' : 'ASC';

  return (
    <button type="button" className={styles['sortable-header']} onClick={() => onSort(sortKey, nextType)}>
      <span>{label}</span>
      <img src={isActive && sortType === 'DESC' ? iconArrowDown : iconArrowUp} alt="" className={styles['sort-icon']} />
    </button>
  );
};

export const FlexboxGridTable = <T extends Record<string, unknown>>({
  data,
  columns,
  loading,
  firstColumnColspan = 9,
  otherColumnsColspan = 5,
  renderTotal = (displayed, total) => `Exibindo ${formatNumberWithThousandsSeparator(displayed)} de ${formatNumberWithThousandsSeparator(total)} solicitações.`,
  onPageChange,
  onSort,
  onClickSources,
}: FlexboxGridTableProps<T>) => {
  const [sortColumn, setSortColumn] = useState('');
  const [sortType, setSortType] = useState<'ASC' | 'DESC'>('DESC');
  const [rows, setRows] = useState<DetailsProdutoContent[]>(data?.content ?? []);

  const handleSort = (column: string, type: 'ASC' | 'DESC') => {
    setSortColumn(column);
    setSortType(type);
    onSort?.(column, type);
  };

  useEffect(() => {
    setRows(data?.content ?? []);
  }, [data]);

  const pageNumber = data?.page?.number ?? 0;
  const activePage = (typeof pageNumber === 'number' ? pageNumber : 0) + 1;
  const total = data?.page?.totalElements ?? 0;
  const displayLength = data?.page?.size ?? 0;

  const getColspan = (index: number, col: FlexboxGridTableColumn<T>) => col.colspan ?? (index === 0 ? firstColumnColspan : otherColumnsColspan);

  return (
    <div className={styles['flexgrid-table-wrapper']}>
      <LoadingSpinerLocal loading={loading} size="md" />
      <FlexboxGrid className={styles['table-header-row']} align="middle">
        {columns.map((col, index) => (
          <FlexboxGrid.Item key={col.key} colspan={getColspan(index, col)} className={styles['table-cell']}>
            {col.sortable && onSort ? (
              <SortableHeader label={col.label} sortKey={col.key} activeSortColumn={sortColumn} sortType={sortType} onSort={handleSort} />
            ) : (
              <span className={styles['header-label']}>{col.label}</span>
            )}
          </FlexboxGrid.Item>
        ))}
      </FlexboxGrid>
      <div className={styles['table-body']}>
        {rows?.map((row: DetailsProdutoContent, rowIndex) => {
          const [col0, col1, col2, col3] = columns;
          const productLabel = `${row?.gtin ?? ''} - ${row?.produto ?? ''}`;
          return (
            <FlexboxGrid key={rowIndex} className={styles['table-row']} align="middle">
              {/* PRODUTO */}
              {col0 && (
                <FlexboxGrid.Item key={col0.key} colspan={getColspan(0, col0)} className={`${styles['table-cell']} ${styles['product-table-cell']}`}>
                  <Whisper trigger="hover" placement="topStart" speaker={<Tooltip>{productLabel}</Tooltip>}>
                    <span className={`${styles['cell-value']} ${styles['product-cell-ellipsis']}`}>{productLabel}</span>
                  </Whisper>
                </FlexboxGrid.Item>
              )}
              {/* PREÇO CANAL FÍSICO */}
              {col1 && (
                <FlexboxGrid.Item key={col1.key} colspan={getColspan(1, col1)} className={styles['table-cell']}>
                  {row?.preco?.canalFisico === null ? (
                    <span className={styles['cell-value']}>--</span>
                  ) : (
                    <span className={styles['cell-value']}>{formatPrice(row?.preco?.canalFisico)}</span>
                  )}
                </FlexboxGrid.Item>
              )}
              {/* PREÇO SITES PRÓPRIOS */}
              {col2 && (
                <FlexboxGrid.Item
                  key={col2.key}
                  colspan={getColspan(2, col2)}
                  className={styles['third-table-cell']}
                  style={{
                    backgroundColor:
                      typeof row?.preco?.sitesProprios?.percentual === 'number'
                        ? getCompetitividadeBackgroundColor(row?.preco?.sitesProprios?.percentual as number)
                        : undefined,
                  }}
                >
                  {row?.preco?.sitesProprios?.valor === null ? (
                    <span className={styles['cell-value']}>--</span>
                  ) : (
                    <>
                      <span className={styles['cell-value']}>{formatPrice(row?.preco?.sitesProprios?.valor)}</span>
                      <span className={styles['cell-value']}>{formatPercent(row?.preco?.sitesProprios?.percentual)}</span>
                    </>
                  )}
                </FlexboxGrid.Item>
              )}
              {/* PREÇO APPS DE ENTREGA */}
              {col3 && (
                <FlexboxGrid.Item
                  key={col3.key}
                  colspan={getColspan(3, col3)}
                  className={styles['last-table-cell']}
                  style={{
                    backgroundColor:
                      typeof row?.preco?.appsEntrega?.percentual === 'number'
                        ? getCompetitividadeBackgroundColor(row?.preco?.appsEntrega?.percentual)
                        : undefined,
                  }}
                >
                  {row?.preco?.appsEntrega?.valor === null ? (
                    <span className={styles['cell-value']}>--</span>
                  ) : (
                    <>
                      <span className={styles['cell-value']}>{formatPrice(row?.preco?.appsEntrega?.valor)}</span>
                      <div className={styles['last-table-cell-content']}>
                        <span className={styles['cell-value']}>{formatPercent(row?.preco?.appsEntrega?.percentual)}</span>
                        <Button appearance="ghost" size="sm" className={styles['view-sources-btn']} onClick={() => onClickSources?.(row)}>
                          Ver fontes
                        </Button>
                      </div>
                    </>
                  )}
                </FlexboxGrid.Item>
              )}
            </FlexboxGrid>
          );
        })}
      </div>
      {data && total > 0 && onPageChange && (
        <Pagination
          className={styles['products-pagination']}
          activePage={activePage}
          displayLength={displayLength}
          showLengthMenu={false}
          total={total}
          onChangePage={(e: number) => onPageChange(e - 1)}
          renderTotal={() => renderTotal(rows?.length ?? 0, total)}
        />
      )}
    </div>
  );
};
